package com.gsb.vyapar.bhagya.presentation.business.uistate

import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateOf
import com.gsb.vyapar.bhagya.datasource.preference.Preference
import com.gsb.vyapar.bhagya.domain.Business
import com.gsb.vyapar.bhagya.domain.Categories.D.Category
import com.gsb.vyapar.bhagya.domain.getCategoryId
import com.gsb.vyapar.bhagya.domain.getId
import com.gsb.vyapar.bhagya.domain.getIndex
import com.gsb.vyapar.bhagya.domain.getSubCategoryIds
import com.gsb.vyapar.bhagya.domain.getUserId
import com.gsb.vyapar.bhagya.interactor.manager.StateManager.getState
import com.gsb.vyapar.bhagya.interactor.manager.StateManager.updateState
import com.gsb.vyapar.bhagya.presentation.business.util.sortedByDistance
import com.gsb.vyapar.bhagya.presentation.category.uistate.getCategoriesViewUiState
import kotlinx.coroutines.flow.MutableStateFlow

data class BusinessUiState(
    val title: String = "Manage Business",
    val mBusinessList: List<Business> = listOf(),
    val isRefreshing: Boolean = false,
    val addEditBusiness: Business = Business(),
    val businessList: List<Business> = listOf(),
    val filteredList: List<Business> = listOf(),
    val business: Business = Business(),
    val myBusinessList: List<Business> = listOf(),
    val favorites: MutableState<String> = mutableStateOf(Preference.favorites),
    val images: List<String> = listOf(),
    val initialPage: Int = 0,
    val businessDetailScroll: Int = 0,
    val loadReviewBusiness: Boolean = false,
    val manageBusinessTab: Int = 0,
    val isBusinessFetched: MutableStateFlow<Map<String, Business>> = MutableStateFlow(emptyMap()),
    val businessMapViewState: BusinessMapViewState = BusinessMapViewState(),
) {
    fun updateMBusinessList(mBusinessList: List<Business>) {
        updateBusinessUiState(this.copy(mBusinessList = mBusinessList))
    }

    fun resetMBusinessList() {
        updateBusinessUiState(this.copy(mBusinessList = listOf()))
    }

    fun updateIsRefreshing(isRefreshing: Boolean) {
        updateBusinessUiState(this.copy(isRefreshing = isRefreshing))
    }

    fun updateAddEditBusiness(addEditBusiness: Business) {
        updateBusinessUiState(this.copy(addEditBusiness = addEditBusiness))
    }

    fun updateBusinessList(businessList: List<Business>) {
        updateBusinessUiState(this.copy(businessList = businessList))
    }

    fun updateBusinessList(business: Business) {
        businessList.find { it.getId() == business.getId() }?.let {
            updateBusinessUiState(this.copy(businessList = ArrayList(businessList - it + business), business = business))
        }
    }

    fun updateFilteredBusinessList(category: Category?) {
        getCategoriesViewUiState().updateCategory(category)
        val filteredList = this.businessList.filter { it.getCategoryId() == category?.id.toString() }.sortedByDistance()
        updateBusinessUiState(this.copy(filteredList = filteredList))
    }

    fun updateFilteredBusinessList(category: Category?, subCategoriesStrings: List<String>) {
        if (subCategoriesStrings.isEmpty()) {
            updateFilteredBusinessList(category)
        } else {
            val subCategories = getState().getSubCategories()
            val filteredSubCategories = subCategories.d.subCategories.filter { subCategoriesStrings.contains(it.name) }
            val filteredList = this.businessList.filter { it.getCategoryId() == category?.id.toString() }.filter { it.getSubCategoryIds().split(", ").mapNotNull { it.toIntOrNull() }.any { id -> filteredSubCategories.any { it.id.toInt() == id } } }.sortedByDistance()
            updateBusinessUiState(this.copy(filteredList = filteredList))
        }
    }

    fun updateFilteredBusinessList(category: Category?, subCategoryName: String) {
        if (subCategoryName.isEmpty()) {
            updateFilteredBusinessList(category)
        } else {
            val subCategories = getState().getSubCategories()
            val filteredSubCategories = subCategories.d.subCategories.filter { subCategoryName == it.name }
            val filteredList = this.businessList.filter { it.getCategoryId() == category?.id.toString() }.filter { it.getSubCategoryIds().split(", ").mapNotNull { it.toIntOrNull() }.any { id -> filteredSubCategories.any { it.id.toInt() == id } } }.sortedByDistance()
            updateBusinessUiState(this.copy(filteredList = filteredList))
        }
    }

    fun updateBusiness(business: Business) {
        updateBusinessUiState(this.copy(business = business))
    }

    fun updateMyBusinessList() {
        val myBusinessList = this.businessList.filter { it.getUserId() == getState().getUser().getId() }
        updateBusinessUiState(this.copy(myBusinessList = myBusinessList))
    }

    fun getLatestBusiness(): List<Business> {
        return try {
            businessList.sortedByDescending { it.getIndex() }
        } catch (e: Exception) {
            listOf()
        }
    }

    fun toggleFavoriteBusiness(business: Business) {
        val favoriteList = favorites.value.split(",").toMutableList()
        if (favoriteList.contains(business.getIndex().toString())) {
            favoriteList.remove(business.getIndex().toString())
        } else {
            favoriteList.add(business.getIndex().toString())
        }
        this.favorites.value = favoriteList.joinToString(",")
        Preference.favorites = favorites.value
    }

    fun updateImages(images: List<String>) {
        updateBusinessUiState(this.copy(images = images))
    }

    fun updateInitialPage(initialPage: Int) {
        updateBusinessUiState(this.copy(initialPage = initialPage))
    }

    fun updateBusinessDetailScroll(scroll: Int) {
        updateBusinessUiState(this.copy(businessDetailScroll = scroll))
    }

    fun updateLoadReviewBusiness(loadReviewBusiness: Boolean) {
        updateBusinessUiState(this.copy(loadReviewBusiness = loadReviewBusiness))
    }

    fun updateManageBusinessTab(tab: Int) {
        updateBusinessUiState(this.copy(manageBusinessTab = tab))
    }

    fun updateBusinessMapCameraPosition(cameraPosition: BusinessMapCameraPosition?) {
        updateBusinessUiState(
            this.copy(
                businessMapViewState = businessMapViewState.copy(cameraPosition = cameraPosition)
            )
        )
    }

    fun updateBusinessMapSelectedBusinessId(selectedBusinessId: String?) {
        updateBusinessUiState(
            this.copy(
                businessMapViewState = businessMapViewState.copy(selectedBusinessId = selectedBusinessId)
            )
        )
    }
}

fun updateBusinessUiState(businessUiState: BusinessUiState) {
    updateState(getState().copy(businessUiState = businessUiState))
}

fun getBusinessUiState(): BusinessUiState {
    return getState().businessUiState
}

fun String.isFavoriteBusiness(business: Business?): Boolean {
    return try {
        this.split(",").toMutableList().contains(business?.getIndex().toString())
    } catch (e: Exception) {
        false
    }
}

fun String.getFavoriteBusiness(): List<Business> {
    return try {
        getBusinessUiState().businessList.filter { this.split(",").contains(it.getIndex().toString()) }
    } catch (e: Exception) {
        listOf()
    }
}
